/**
 * StationCheckIn — student-side live telemetry for the Teacher Flight Control board.
 * Lets a student pick their grade, broadcast "working / stuck / completed" during
 * station rotation, and submit their end-of-block Exit Ticket (mastery self-rating
 * + reflection). Feeds /academy/flight-plan/station-status & /mastery in real time.
 */
import React, { useEffect, useState } from 'react';
import { CircleDot, AlertTriangle, CheckCircle2, Send, Loader2, Radio } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const GRADES = [4, 5, 6, 7, 8, 9];

const StationCheckIn = () => {
  const [grade, setGrade] = useState(() => Number(localStorage.getItem('saa_grade')) || 0);
  const [status, setStatus] = useState(null);
  const [ticketDone, setTicketDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mastery, setMastery] = useState(0);
  const [reflection, setReflection] = useState('');
  const [showTicket, setShowTicket] = useState(false);

  useEffect(() => {
    api.get('/academy/student/checkin', { headers: authHeaders() })
      .then((r) => {
        if (r.data.checkin) setStatus(r.data.checkin.status);
        if (r.data.exit_ticket) setTicketDone(true);
      })
      .catch(() => {});
    // The student's enrolled grade is authoritative for the check-in default (grades 4-9).
    api.get('/academy/auth/me', { headers: authHeaders() })
      .then((r) => {
        const g = Number((r.data.user || r.data)?.grade_level);
        if (g >= 4 && g <= 9) {
          setGrade(g);
          localStorage.setItem('saa_grade', String(g));
        }
      })
      .catch(() => {});
  }, []);

  const sendStatus = async (next) => {
    if (!grade) { toast.error('Select your grade first'); return; }
    setBusy(true);
    localStorage.setItem('saa_grade', String(grade));
    try {
      await api.post('/academy/student/checkin', { grade, status: next }, { headers: authHeaders() });
      setStatus(next);
      toast.success(`Sent: ${next}`);
    } catch (e) {
      toast.error('Could not send check-in');
    } finally { setBusy(false); }
  };

  const submitTicket = async () => {
    if (!mastery) { toast.error('Pick your mastery level'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/student/exit-ticket', { grade, mastery, reflection }, { headers: authHeaders() });
      setTicketDone(true);
      setShowTicket(false);
      toast.success(r.data.status === 'pass' ? '✅ Exit ticket: Pass' : '📝 Exit ticket: flagged for review');
    } catch (e) {
      toast.error('Could not submit exit ticket');
    } finally { setBusy(false); }
  };

  const STATUSES = [
    { key: 'working', label: "I'm Working", icon: CircleDot, cls: 'border-sky-500 text-sky-300', active: 'bg-sky-500 text-black' },
    { key: 'stuck', label: "I'm Stuck", icon: AlertTriangle, cls: 'border-red-500 text-red-300', active: 'bg-red-500 text-black' },
    { key: 'completed', label: 'Module Done', icon: CheckCircle2, cls: 'border-emerald-500 text-emerald-300', active: 'bg-emerald-500 text-black' },
  ];

  return (
    <div className="mb-6 border-2 border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="student-station-checkin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-emerald-400">
          <Radio className="w-4 h-4 animate-pulse" />
          <span className="text-xs font-black tracking-[0.2em] uppercase">Station Check-In</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Grade</span>
          <select
            value={grade || ''}
            onChange={(e) => { setGrade(Number(e.target.value)); localStorage.setItem('saa_grade', e.target.value); }}
            className="bg-black border-2 border-zinc-700 px-2 py-1 text-sm font-bold text-white focus:border-emerald-500 outline-none"
            data-testid="checkin-grade"
          >
            {!grade && <option value="" disabled>Select…</option>}
            {GRADES.map((g) => <option key={g} value={g}>Grade {g}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        {STATUSES.map((s) => {
          const Icon = s.icon;
          const isActive = status === s.key;
          return (
            <button
              key={s.key}
              onClick={() => sendStatus(s.key)}
              disabled={busy}
              className={`flex flex-col items-center gap-1 py-3 border-2 text-[11px] font-black uppercase tracking-wider transition-colors disabled:opacity-50 ${isActive ? s.active : s.cls + ' hover:bg-white/5'}`}
              data-testid={`checkin-${s.key}`}
            >
              <Icon className="w-5 h-5" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-800">
        {ticketDone ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold" data-testid="exit-ticket-done">
            <CheckCircle2 className="w-4 h-4" /> Exit ticket submitted for today.
          </div>
        ) : !showTicket ? (
          <button
            onClick={() => setShowTicket(true)}
            className="w-full flex items-center justify-center gap-2 border-2 border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-black py-3 text-xs font-black uppercase tracking-[0.2em] transition-colors"
            data-testid="open-exit-ticket"
          >
            <Send className="w-4 h-4" /> Submit Exit Ticket
          </button>
        ) : (
          <div className="space-y-3" data-testid="exit-ticket-form">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">How well did you master today's standard?</div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((m) => (
                <button
                  key={m}
                  onClick={() => setMastery(m)}
                  className={`py-2 border-2 text-sm font-black transition-colors ${mastery === m ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-zinc-700 text-zinc-400 hover:border-emerald-500'}`}
                  data-testid={`mastery-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="text-[9px] text-zinc-600 uppercase tracking-wider">1 = still learning · 4 = mastered (3+ passes)</div>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="One line: how would you use this in your sport?"
              rows={2}
              className="w-full bg-black border-2 border-zinc-700 px-3 py-2 text-sm text-white focus:border-emerald-500 outline-none"
              data-testid="exit-ticket-reflection"
            />
            <div className="flex gap-2">
              <button onClick={submitTicket} disabled={busy} className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-black py-3 text-xs font-black uppercase tracking-[0.2em] hover:bg-emerald-400 disabled:opacity-50 transition-colors" data-testid="submit-exit-ticket">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Turn In
              </button>
              <button onClick={() => setShowTicket(false)} className="px-4 border-2 border-zinc-700 text-zinc-400 text-xs font-black uppercase hover:border-white transition-colors">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StationCheckIn;
